import React, { useState, useEffect } from 'react';
import { AdminSidebar } from '@/components/AdminSidebar';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Form, FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from '@/components/ui/form';
import { blogPosts } from '@/data/blogPosts';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import PageTransition from '@/components/PageTransition';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { BlogPost } from '@/data/blogPosts';

// Create a schema for form validation
const blogPostSchema = z.object({
  title: z.string().min(5, { message: "Title must be at least 5 characters" }),
  excerpt: z.string().min(10, { message: "Excerpt must be at least 10 characters" }),
  content: z.string().min(50, { message: "Content must be at least 50 characters" }),
  coverImage: z.string().url({ message: "Please enter a valid URL for the cover image" }),
  author: z.string().min(2, { message: "Author name is required" }),
  categories: z.string().min(3, { message: "Please provide at least one category" }),
  published: z.boolean().default(false),
});

const BlogPostEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;
  
  // Set up form with react-hook-form and zod validation
  const form = useForm({
    resolver: zodResolver(blogPostSchema),
    defaultValues: {
      title: '',
      excerpt: '',
      content: '',
      coverImage: 'https://images.unsplash.com/photo-1501139083538-0139583c060f?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80',
      author: '',
      categories: '',
      published: false,
    }
  });

  // Load post data if editing
  useEffect(() => {
    if (isEditing) {
      const post = blogPosts.find(post => post.id === Number(id));
      if (post) {
        form.reset({
          title: post.title,
          excerpt: post.excerpt,
          content: post.content,
          coverImage: post.coverImage,
          author: post.author,
          categories: post.categories.join(', '),
          published: post.published,
        });
      } else {
        toast.error('Post not found');
        navigate('/blog-admin');
      }
    }
  }, [id, isEditing, navigate]);

  // Handle form submission
  const onSubmit = (data) => {
    // In a real app, this would send the data to an API
    // For now, we'll just show a success message
    
    // Create a blog post object from form data
    const blogPost: Partial<BlogPost> = {
      title: data.title,
      excerpt: data.excerpt,
      content: data.content,
      coverImage: data.coverImage,
      author: data.author,
      categories: data.categories.split(',').map(cat => cat.trim()),
      published: data.published,
      date: new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }),
      slug: data.title
        .toLowerCase()
        .replace(/[^\w\s]/gi, '')
        .replace(/\s+/g, '-'),
    };

    // In a real app with a backend, we would save to a database here
    // For demo purposes, just show a toast
    if (isEditing) {
      toast.success(`Post "${data.title}" updated successfully!`);
    } else {
      toast.success(`Post "${data.title}" created successfully!`);
    }
    
    navigate('/blog-admin');
  };

  return (
    <div className="flex min-h-screen">
      <AdminSidebar />
      
      <div className="flex-1 p-6 overflow-y-auto">
        <PageTransition>
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold">Admin - {isEditing ? 'Edit Post' : 'Create New Post'}</h1>
            <Button variant="outline" onClick={() => navigate('/blog-admin')}>
              Cancel
            </Button>
          </div>
          
          <Separator className="mb-6" />
          
          <Card className="p-6">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter post title" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="excerpt"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Excerpt</FormLabel>
                      <FormControl>
                        <Input placeholder="Brief summary of the post" {...field} />
                      </FormControl>
                      <FormDescription>
                        A short preview of your post (shown in listings)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="content"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Content</FormLabel>
                      <FormControl>
                        <textarea 
                          className="w-full min-h-[200px] p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="Enter post content (HTML is supported)"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="coverImage"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Cover Image URL</FormLabel>
                        <FormControl>
                          <Input placeholder="https://example.com/image.jpg" {...field} />
                        </FormControl>
                        <FormDescription>
                          URL to the main image for this post
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="author"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Author</FormLabel>
                        <FormControl>
                          <Input placeholder="Post author" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                
                <FormField
                  control={form.control}
                  name="categories"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Categories</FormLabel>
                      <FormControl>
                        <Input placeholder="Emergency, Preparedness, Planning" {...field} />
                      </FormControl>
                      <FormDescription>
                        Comma-separated list of categories
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="published"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">
                          Published
                        </FormLabel>
                        <FormDescription>
                          Toggle to publish or save as draft
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                
                <div className="flex justify-end space-x-4 pt-4">
                  <Button type="submit" size="lg">
                    {isEditing ? 'Update Post' : 'Create Post'}
                  </Button>
                </div>
              </form>
            </Form>
          </Card>
        </PageTransition>
      </div>
    </div>
  );
};

export default BlogPostEditor;
